﻿import { useState, useMemo, useEffect } from 'react';
import { Button, Typography, Input, Space, Popconfirm, message, Tree, Modal, Tooltip, Tag, InputNumber, Switch } from 'antd';
import {
  EyeOutlined,
  EyeInvisibleOutlined,
  LockOutlined,
  UnlockOutlined,
  DeleteOutlined,
  PlusOutlined,
  FolderOutlined,
  FileOutlined,
  GroupOutlined,
  LinkOutlined,
  UpOutlined,
  DownOutlined,
  AimOutlined,
  VerticalAlignTopOutlined,
  BorderBottomOutlined,
  DashboardOutlined,
  ControlOutlined,
  DisconnectOutlined,
} from '@ant-design/icons';
import useSceneStore from '../../stores/sceneStore';
import useLayerStore, { type Layer, collectLayerModelIds } from '../../stores/layerStore';
import useEditorUiStore from '../../stores/editorUiStore';
import useDashboardStore from '../../stores/dashboardStore';
import { updateModel as apiUpdateModel } from '../../api/models';
import { updatePage } from '../../api/pages';
import type { DataNode } from 'antd/es/tree';
import type { SceneAnchor } from '@dtwin/shared';
import { broadcastFusion } from '../../utils/fusionSync';
import ComponentPanel from '../dashboard/ComponentPanel';

const { Text } = Typography;

/** 场景↔大屏绑定默认锚点（与 SceneEditor 的 DEFAULT_SCENE_ANCHOR 保持一致） */
const DEFAULT_ANCHOR: SceneAnchor = {
  position: { x: 0, y: 0, z: 0 },
  rotation: { x: 0, y: 0, z: 0 },
  scale: 1,
  visible: true,
};

export function LayerPanel() {
  const { models, selectedModelId, selectModel } = useSceneStore();
  // 阶段1.5｜G10 混合图层树：2D 大屏图层与 3D 模型图层统一展示（纯 UI 合成，数据模型零改动）
  const activeEditor = useEditorUiStore((s) => s.activeEditor);
  const dashboardId = useEditorUiStore((s) => s.dashboardId);
  const overlayPreview = useEditorUiStore((s) => s.overlayPreview);
  const overlayEditMode = useEditorUiStore((s) => s.overlayEditMode);
  const layerCardHighlightAt = useEditorUiStore((s) => s.layerCardHighlightAt);
  const [cardFlash, setCardFlash] = useState(false);
  const currentPage = useDashboardStore((s) => s.currentPage);
  // 2026-10-11：组件计数用实时 store.components（页面快照的 components 不随编辑更新会显示旧数量）
  const dashComponents = useDashboardStore((s) => s.components);
  const {
    layers,
    selectedLayerId,
    selectLayer,
    addLayer,
    removeLayer,
    updateLayer,
    toggleVisibility,
    toggleLock,
    addModelToLayer,
    removeModelFromLayer,
    reorderLayers,
  } = useLayerStore();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [showAddGroupModal, setShowAddGroupModal] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [showAddLayerModal, setShowAddLayerModal] = useState(false);
  const [newLayerName, setNewLayerName] = useState('');
  // 2026-09-08｜B-06：可视化图层锚点编辑（位置/缩放/可见性，随 page.sceneAnchor 持久化）
  const [anchorOpen, setAnchorOpen] = useState(false);
  const [anchor, setAnchor] = useState<SceneAnchor>(DEFAULT_ANCHOR);

  // 2026-10-05：融合菜单「图层融合设置」定位卡片 → 高亮闪烁 2.5s
  useEffect(() => {
    if (!layerCardHighlightAt) return;
    setCardFlash(true);
    const t = setTimeout(() => setCardFlash(false), 2500);
    return () => clearTimeout(t);
  }, [layerCardHighlightAt]);

  const openAnchorEditor = () => {    const pg = useDashboardStore.getState().currentPage;
    setAnchor(pg?.sceneAnchor || DEFAULT_ANCHOR);
    setAnchorOpen(true);
  };

  const saveAnchor = async () => {
    if (!dashboardId) return;
    try {
      await updatePage(dashboardId, { sceneAnchor: anchor } as any);
      const pg = useDashboardStore.getState().currentPage;
      if (pg) useDashboardStore.setState({ currentPage: { ...pg, sceneAnchor: anchor } as any });
      message.success('锚点已保存');
      setAnchorOpen(false);
    } catch {
      message.error('锚点保存失败');
    }
  };

  const handleUnlink = async () => {
    if (!dashboardId) return;
    try {
      await updatePage(dashboardId, { sceneId: null } as any);
      useEditorUiStore.getState().setDashboardId(null);
      useEditorUiStore.getState().setOverlayPreview(false);
      broadcastFusion({ type: 'page-scene-linked', pageId: dashboardId, sceneId: null, source: 'scene' });
      message.success('已解除大屏关联');
    } catch {
      message.error('解除关联失败');
    }
  };

  const handleRename = (id: string) => {
    if (editingName.trim()) {
      updateLayer(id, { name: editingName.trim() });
    }
    setEditingId(null);
    setEditingName('');
  };

  const handleDelete = (id: string) => {
    const layer = layers.find((l) => l.id === id);
    if (layer?.modelIds?.length) {
      layer.modelIds.forEach((mid) => {
        useSceneStore.getState().updateModel(mid, { visible: true }, false);
      });
    }
    removeLayer(id);
    message.success('已删除');
  };

  const handleToggleVisibility = (layerId: string) => {
    toggleVisibility(layerId);
    const layer = layers.find((l) => l.id === layerId);
    if (!layer) return;
    const newVisible = !layer.visible;
    collectLayerModelIds(layers, layerId).forEach((mid) => {
      useSceneStore.getState().updateModel(mid, { visible: newVisible }, false);
      apiUpdateModel(mid, { visible: newVisible } as any).catch(() => {});
    });
  };

  // 图层/组锁定：锁定图层或组 → 其下（含递归子图层/子组）所有模型全部锁定
  const handleToggleLock = (layerId: string) => {
    toggleLock(layerId);
    const layer = layers.find((l) => l.id === layerId);
    if (!layer) return;
    const newLocked = !layer.locked;
    collectLayerModelIds(layers, layerId).forEach((mid) => {
      useSceneStore.getState().setLocked(mid, newLocked);
      apiUpdateModel(mid, { locked: newLocked } as any).catch(() => {});
    });
  };

  const startRename = (id: string, currentName: string) => {
    setEditingId(id);
    setEditingName(currentName);
  };

  const handleAddGroup = () => {
    if (newGroupName.trim()) {
      addLayer({
        name: newGroupName.trim(),
        type: 'group',
        visible: true,
        locked: false,
        modelIds: [],
      });
      setNewGroupName('');
      setShowAddGroupModal(false);
      message.success('已添加分组');
    }
  };

  const handleAddLayer = () => {
    if (newLayerName.trim()) {
      addLayer({
        name: newLayerName.trim(),
        type: '3d-model',
        visible: true,
        locked: false,
        modelIds: [],
      });
      setNewLayerName('');
      setShowAddLayerModal(false);
      message.success('已添加图层');
    }
  };

  const handleJoinModel = () => {
    if (selectedLayerId && selectedModelId) {
      addModelToLayer(selectedLayerId, selectedModelId);
      message.success('模型已加入图层');
    }
  };

  // 图层排序（针对选中的图层/分组）
  const handleSortLayer = (dir: 'up' | 'down' | 'top' | 'bottom') => {
    if (!selectedLayerId) {
      message.info('请先选中要排序的图层');
      return;
    }
    const idx = layers.findIndex((l) => l.id === selectedLayerId);
    if (idx < 0) return;
    if (dir === 'top' && idx === 0) return;
    if (dir === 'bottom' && idx === layers.length - 1) return;
    if (dir === 'up' && idx === 0) return;
    if (dir === 'down' && idx === layers.length - 1) return;
    if (dir === 'up') reorderLayers(idx, idx - 1);
    else if (dir === 'down') reorderLayers(idx, idx + 1);
    else if (dir === 'top') reorderLayers(idx, 0);
    else reorderLayers(idx, layers.length - 1);
  };

  const handleUnlinkModel = (layerId: string, modelId: string) => {
    removeModelFromLayer(layerId, modelId);
    message.success('已取消关联');
  };

  const convertToTreeData = (layers: Layer[]): DataNode[] => {
    return layers.map((layer) => ({
      title: (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
          {layer.type === 'group' ? (
            <FolderOutlined style={{ color: '#faad14', fontSize: 12 }} />
          ) : layer.type === '3d-model' ? (
            <FileOutlined style={{ color: '#1890ff', fontSize: 12 }} />
          ) : (
            <GroupOutlined style={{ color: '#52c41a', fontSize: 12 }} />
          )}

          {editingId === layer.id ? (
            <Input
              size="small"
              value={editingName}
              onChange={(e) => setEditingName(e.target.value)}
              onBlur={() => handleRename(layer.id)}
              onPressEnter={() => handleRename(layer.id)}
              autoFocus
              style={{ flex: 1 }}
            />
          ) : (
            <Text
              ellipsis
              style={{ flex: 1, fontSize: 12, color: '#e0e0e0' }}
              onDoubleClick={() => startRename(layer.id, layer.name)}
            >
              {layer.name}
              {(layer.modelIds?.length ?? 0) > 0 && (
                <span style={{ color: '#888', marginLeft: 4 }}>
                  ({layer.modelIds!.length})
                </span>
              )}
            </Text>
          )}

          <Space size={4}>
            <Button
              type="text"
              size="small"
              icon={layer.visible ? <EyeOutlined /> : <EyeInvisibleOutlined />}
              onClick={(e) => { e.stopPropagation(); handleToggleVisibility(layer.id); }}
              style={{ color: layer.visible ? '#888' : '#555' }}
            />
            <Button
              type="text"
              size="small"
              icon={layer.locked ? <LockOutlined /> : <UnlockOutlined />}
              onClick={(e) => { e.stopPropagation(); handleToggleLock(layer.id); }}
              style={{ color: '#888' }}
            />
            <Popconfirm
              title="确认删除？"
              description={layer.modelIds?.length ? `该图层关联了 ${layer.modelIds.length} 个模型，删除后关联将解除` : undefined}
              onConfirm={() => handleDelete(layer.id)}
            >
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={(e) => e.stopPropagation()}
              />
            </Popconfirm>
          </Space>
        </div>
      ),
      key: layer.id,
      children: layer.children
        ? convertToTreeData(layers.filter((l) => layer.children?.includes(l.id)))
        : (layer.modelIds ?? []).length > 0
          ? layer.modelIds!.map((mid) => ({
              key: `model-${mid}`,
              title: (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#aaa' }}>
                  <FileOutlined style={{ color: '#1890ff', fontSize: 10 }} />
                  <span>{models.find((m) => m.id === mid)?.name || mid}</span>
                  <Button
                    type="text"
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={(e) => { e.stopPropagation(); handleUnlinkModel(layer.id, mid); }}
                    style={{ color: '#ff4d4f', fontSize: 10, padding: 0, width: 16, height: 16 }}
                  />
                </div>
              ),
              isLeaf: true,
            }))
          : undefined,
    }));
  };

  const totalModelCount = layers.reduce((sum, l) => sum + (l.modelIds?.length || 0), 0);

  return (
    <div style={{ padding: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <Text strong style={{ fontSize: 12, color: '#888' }}>
          图层管理
        </Text>
        <Space>
          <Button
            type="text"
            size="small"
            icon={<PlusOutlined />}
            onClick={() => setShowAddLayerModal(true)}
          >
            新建图层
          </Button>
          <Button
            type="text"
            size="small"
            icon={<FolderOutlined />}
            onClick={() => setShowAddGroupModal(true)}
          >
            新建分组
          </Button>
          <Tooltip title="上移选中图层">
            <Button type="text" size="small" icon={<UpOutlined />} onClick={() => handleSortLayer('up')} />
          </Tooltip>
          <Tooltip title="下移选中图层">
            <Button type="text" size="small" icon={<DownOutlined />} onClick={() => handleSortLayer('down')} />
          </Tooltip>
          <Tooltip title="置顶">
            <Button type="text" size="small" icon={<VerticalAlignTopOutlined />} onClick={() => handleSortLayer('top')} />
          </Tooltip>
          <Tooltip title="置底">
            <Button type="text" size="small" icon={<BorderBottomOutlined />} onClick={() => handleSortLayer('bottom')} />
          </Tooltip>
          <Text style={{ fontSize: 11, color: '#555' }}>
            {totalModelCount} / {layers.length}
          </Text>
        </Space>
      </div>

      {/* 阶段1.5｜G10 混合图层树：2D 大屏图层条目（与 3D 模型图层混排；纯 UI 合成，数据模型零改动）
        交互语义（Q2 修复）：未关联 → 点击弹出「关联/新建大屏」窗口；已关联（scene 态）→ 只读预览叠加；
        「激活编辑」按钮进入 2D 编辑态 */}
      <div
        data-testid="dash-visual-layer"
        data-flash={cardFlash ? '1' : '0'}
        style={{
          marginBottom: 10, border: '1px dashed #3d5a80', borderRadius: 6, padding: '8px 10px',
          background: overlayPreview ? '#1a2a3f' : '#14141f',
          cursor: activeEditor !== 'dashboard' ? 'pointer' : 'default',
          outline: cardFlash ? '2px solid #faad14' : 'none',
          transition: 'outline-color .25s',
        }}
        onClick={() => {
          if (activeEditor === 'dashboard') return;
          const st = useEditorUiStore.getState();
          if (!dashboardId) {
            st.setDashboardBindOpen(true);
            return;
          }
          if (overlayPreview) st.setOverlayPreview(false);
          else st.setOverlayPreview(true);
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <DashboardOutlined style={{ color: '#52c41a', fontSize: 12 }} />
          <Text strong style={{ fontSize: 12, color: '#888' }}>大屏图层</Text>
          {dashboardId && (
            <Text ellipsis style={{ flex: 1, fontSize: 12, color: '#e0e0e0' }}>
              {currentPage?.name || dashboardId.slice(0, 8)}
              {currentPage ? (
                <span style={{ color: '#888', marginLeft: 4 }}>({dashComponents.length})</span>
              ) : null}
            </Text>
          )}
          {dashboardId && (
            <>
              <Tooltip title="解除大屏与场景的关联">
                <Button
                  size="small"
                  danger
                  icon={<DisconnectOutlined />}
                  onClick={(e) => { e.stopPropagation(); handleUnlink(); }}
                >
                  解除
                </Button>
              </Tooltip>
              {activeEditor === 'dashboard' ? (
                <Tag color="green" style={{ marginInlineEnd: 0 }}>编辑中</Tag>
              ) : overlayPreview ? (
                <Button
                  size="small"
                  type="primary"
                  ghost
                  data-testid="dash-readonly-toggle"
                  onClick={(e) => { e.stopPropagation(); useEditorUiStore.getState().setOverlayPreview(false); }}
                >
                  结束预览
                </Button>
              ) : (
                <Tooltip title="只读预览叠加（点击切换）">
                  <Button
                    size="small"
                    data-testid="dash-readonly-toggle"
                    onClick={(e) => { e.stopPropagation(); useEditorUiStore.getState().setOverlayPreview(true); }}
                  >
                    只读
                  </Button>
                </Tooltip>
              )}
            </>
          )}
        </div>
        {dashboardId ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
              <Tooltip title="可视化图层锚点（位置/缩放/可见性）">
                <Button
                  size="small"
                  icon={<ControlOutlined />}
                  onClick={(e) => { e.stopPropagation(); openAnchorEditor(); }}
                >
                  锚点
                </Button>
              </Tooltip>
              {activeEditor === 'dashboard' ? (
                <Button
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    useEditorUiStore.getState().enterSceneEditor();
                  }}
                >
                  返回3D
                </Button>
              ) : (
                <Button
                  size="small"
                  type="primary"
                  data-testid="dash-edit-page"
                  onClick={(e) => {
                    e.stopPropagation();
                    useEditorUiStore.getState().enterDashboardEditor();
                  }}
                >
                  编辑大屏
                </Button>
              )}
            </div>
            {/* 单独一行，避免面板窄时按钮被截断 */}
            <Button
              size="small"
              block
              icon={<AimOutlined />}
              data-testid="dash-enter-overlay-edit"
              disabled={activeEditor === 'dashboard'}
              style={{ marginTop: 6 }}
              onClick={(e) => {
                e.stopPropagation();
                useEditorUiStore.getState().setOverlayEditMode(true);
              }}
            >
              大屏叠加编辑
            </Button>
          </>
        ) : (
          <Text style={{ fontSize: 11, color: '#555', display: 'block' }}>
            未关联大屏 — 点击此处关联/新建大屏（2D 图层叠加在 3D 之上）
          </Text>
        )}
      </div>

      {/* 2D 大屏编辑器组件库：仅在大屏叠加编辑态显示（scene 只读态把组件加进去会看不到 3D 视口效果） */}
      {dashboardId && overlayEditMode && (
        <div
          data-testid="dash-widget-library"
          style={{ marginBottom: 10, border: '1px solid #2f4f77', borderRadius: 6, background: '#14141f', overflow: 'hidden' }}
        >
          <ComponentPanel />
        </div>
      )}

      {selectedModelId && selectedLayerId && (
        <div style={{ marginBottom: 8 }}>
          <Button
            type="dashed"
            size="small"
            icon={<LinkOutlined />}
            onClick={handleJoinModel}
            block
            disabled={
              layers
                .find((l) => l.id === selectedLayerId)
                ?.modelIds?.includes(selectedModelId)
            }
          >
            将选中模型加入此图层
          </Button>
        </div>
      )}

      <Tree
        showIcon
        defaultExpandAll
        selectedKeys={selectedLayerId ? [selectedLayerId] : []}
        onSelect={(selectedKeys) => {
          if (selectedKeys.length > 0 && !(selectedKeys[0] as string).startsWith('model-')) {
            selectLayer(selectedKeys[0] as string);
          }
        }}
        treeData={convertToTreeData(layers)}
        style={{ fontSize: 12 }}
      />

      <Modal
        title="新建分组"
        open={showAddGroupModal}
        onOk={handleAddGroup}
        onCancel={() => setShowAddGroupModal(false)}
        okText="确定"
        cancelText="取消"
      >
        <Input
          placeholder="请输入分组名称"
          value={newGroupName}
          onChange={(e) => setNewGroupName(e.target.value)}
          onPressEnter={handleAddGroup}
        />
      </Modal>

      <Modal
        title="新建图层"
        open={showAddLayerModal}
        onOk={handleAddLayer}
        onCancel={() => setShowAddLayerModal(false)}
        okText="确定"
        cancelText="取消"
      >
        <Input
          placeholder="请输入图层名称"
          value={newLayerName}
          onChange={(e) => setNewLayerName(e.target.value)}
          onPressEnter={handleAddLayer}
        />
      </Modal>

      {/* 2026-09-08｜B-06：可视化图层锚点编辑（随 page.sceneAnchor 持久化） */}
      <Modal
        title="可视化图层锚点"
        open={anchorOpen}
        onOk={saveAnchor}
        onCancel={() => setAnchorOpen(false)}
        okText="保存"
        cancelText="取消"
      >
        <Space orientation="vertical" style={{ width: '100%' }} size={12}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
            <div>
              <Text style={{ fontSize: 12, color: '#888' }}>位置 X</Text>
              <InputNumber size="small" style={{ width: '100%' }} value={anchor.position.x}
                onChange={(v) => setAnchor({ ...anchor, position: { ...anchor.position, x: v ?? 0 } })} />
            </div>
            <div>
              <Text style={{ fontSize: 12, color: '#888' }}>位置 Y</Text>
              <InputNumber size="small" style={{ width: '100%' }} value={anchor.position.y}
                onChange={(v) => setAnchor({ ...anchor, position: { ...anchor.position, y: v ?? 0 } })} />
            </div>
            <div>
              <Text style={{ fontSize: 12, color: '#888' }}>位置 Z</Text>
              <InputNumber size="small" style={{ width: '100%' }} value={anchor.position.z}
                onChange={(v) => setAnchor({ ...anchor, position: { ...anchor.position, z: v ?? 0 } })} />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
            <div>
              <Text style={{ fontSize: 12, color: '#888' }}>旋转 Y</Text>
              <InputNumber size="small" style={{ width: '100%' }} value={anchor.rotation.y}
                onChange={(v) => setAnchor({ ...anchor, rotation: { ...anchor.rotation, y: v ?? 0 } })} />
            </div>
            <div>
              <Text style={{ fontSize: 12, color: '#888' }}>缩放</Text>
              <InputNumber size="small" style={{ width: '100%' }} min={0.1} max={10} step={0.1} value={anchor.scale}
                onChange={(v) => setAnchor({ ...anchor, scale: v ?? 1 })} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Text style={{ fontSize: 12, color: '#888' }}>可见</Text>
              <Switch size="small" checked={anchor.visible}
                onChange={(v) => setAnchor({ ...anchor, visible: v })} />
            </div>
          </div>
          <Text style={{ fontSize: 11, color: '#555' }}>
            锚点控制 2D 大屏在 3D 场景中的叠加位置/缩放/可见性；只读预览与导出时生效。
          </Text>
        </Space>
      </Modal>
    </div>
  );
}

export default LayerPanel;
